import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Truck, 
  Shield, 
  Zap, 
  MapPin, 
  CreditCard, 
  Users, 
  CheckCircle, 
  ArrowRight,
  Building2,
  Clock,
  FileCheck,
  Route,
  Star,
  Download,
  Smartphone,
  MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useRegion } from '@/context/RegionContext';
import { RegionSelector } from '@/components/RegionSelector';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cliente');
  const [showIOSModal, setShowIOSModal] = useState(false);
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const { config, formatCurrency } = useRegion();

  const handleInstallApp = async () => {
    if (isIOS) {
      setShowIOSModal(true);
    } else if (isInstallable) {
      await promptInstall();
    } else {
      navigate('/login');
    }
  };

  const features = [
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Gestão Completa de Entregas",
      description: "Controle todo o ciclo da entrega, desde a criação até a confirmação final com código de verificação."
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Integração com Mapas",
      description: "Geocodificação automática e rotas otimizadas via Google Maps, Waze ou OpenStreetMap."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Validação Cadastral",
      description: `Sistema de aprovação de empresas com verificação de documentos e ${config.document.name} ativo.`
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Sistema Pré-pago",
      description: "Controle financeiro com assinaturas e créditos por entrega. Sem surpresas."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Múltiplos Perfis",
      description: `Gestão de clientes, ${config.terms.entregadores} e funcionários com permissões específicas.`
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Atribuição Inteligente",
      description: `Entregas direcionadas ou abertas para o primeiro ${config.terms.entregador} disponível.`
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Cadastre sua Empresa",
      description: `Crie sua conta com ${config.document.name}, envie os documentos e aguarde a validação.`,
      icon: <Building2 className="w-8 h-8" />
    },
    {
      step: "02",
      title: "Ative sua Assinatura",
      description: "Escolha seu plano e compre créditos para começar a operar.",
      icon: <CreditCard className="w-8 h-8" />
    },
    {
      step: "03",
      title: "Crie suas Entregas",
      description: `Cadastre entregas com endereço de destino e escolha o ${config.terms.entregador}.`,
      icon: <FileCheck className="w-8 h-8" />
    },
    {
      step: "04",
      title: "Acompanhe em Tempo Real",
      description: "Monitore o status e confirme a entrega com código de 6 dígitos.",
      icon: <Route className="w-8 h-8" />
    }
  ];

  // Preços por região
  const prices = {
    BR: { cliente: '149,90', entregador: '49,90', taxa: '1,50' },
    PT: { cliente: '29,90', entregador: '12,90', taxa: '0,30' }
  };
  
  const currentPrices = prices[config.code] || prices.BR;

  const plans = {
    cliente: {
      name: "Plano Cliente",
      subtitle: "Para quem solicita entregas",
      price: currentPrices.cliente,
      period: "/mês",
      features: [
        "Gestão ilimitada de entregas",
        "Painel de controle completo",
        "Relatórios e histórico",
        "Código de confirmação",
        "Suporte via chat",
        "Controle de recebíveis"
      ],
      extra: `+ ${config.currency.symbol} ${currentPrices.taxa} por entrega (créditos)`,
      color: "orange"
    },
    entregador: {
      name: config.code === 'PT' ? "Plano Estafeta" : "Plano Entregador",
      subtitle: "Para quem realiza entregas",
      price: currentPrices.entregador,
      period: "/semana",
      features: [
        "Receba entregas ilimitadas",
        "App com rotas integradas",
        "Cadastro de funcionários",
        "Gestão de equipe",
        "Notificações em tempo real",
        "Suporte prioritário"
      ],
      bonuses: config.code === 'BR' ? [
        { icon: "🏥", text: "Telemedicina gratuita para assinantes", highlight: true },
        { icon: "🛢️", text: "300 entregas/mês = Troca de óleo grátis", highlight: false },
        { icon: "⚙️", text: "600+ entregas/mês = Troca de relação completa", highlight: false }
      ] : [
        { icon: "🏥", text: "Telemedicina gratuita para assinantes", highlight: true },
        { icon: "🎁", text: "Bónus por metas de entregas", highlight: false }
      ],
      extra: "Sem taxa por entrega",
      color: "green"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover" />
              <div>
                <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Rota 017
                </span>
                <span className="hidden sm:inline text-xs text-slate-500 ml-2">Seu pedido, nossa rota!</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-slate-600 hover:text-orange-600 transition-colors">Recursos</a>
              <a href="#how-it-works" className="text-sm text-slate-600 hover:text-orange-600 transition-colors">Como Funciona</a>
              <a href="#pricing" className="text-sm text-slate-600 hover:text-orange-600 transition-colors">Planos</a>
            </nav>
            
            <div className="flex items-center gap-3">
              <RegionSelector variant="minimal" />
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-slate-600 hover:text-orange-600"
              >
                Entrar
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700"
              >
                Começar Agora
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-amber-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Entregas Rápidas e Seguras
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Entregas
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500"> B2B</span>
                <br />para seu Negócio
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 max-w-lg">
                Conecte sua empresa a {config.terms.entregadores} verificados. Ideal para restaurantes, farmácias, lojas, supermercados, floristas e muito mais.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700 h-14 px-8 text-lg"
                >
                  Criar Conta Grátis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg border-slate-300 hover:border-orange-300 hover:text-orange-600"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver Como Funciona
                </Button>
              </div>
              
              <div className="flex items-center gap-8 mt-10 pt-10 border-t border-slate-200">
                <div>
                  <p className="text-3xl font-bold text-slate-900">500+</p>
                  <p className="text-sm text-slate-500">Empresas Ativas</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">10k+</p>
                  <p className="text-sm text-slate-500">Entregas/mês</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-slate-900">99%</p>
                  <p className="text-sm text-slate-500">Satisfação</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-orange-600 to-amber-500 rounded-3xl p-8 shadow-2xl shadow-orange-500/20">
                <div className="bg-white rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">Pedido #4521</span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Em entrega</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Origem</p>
                        <p className="text-sm font-medium">Farmácia Central</p>
                      </div>
                    </div>
                    
                    <div className="ml-4 border-l-2 border-dashed border-orange-200 h-6"></div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Destino</p>
                        <p className="text-sm font-medium">Escritório Central LTDA</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white text-xs font-bold">JS</div>
                      <div>
                        <p className="text-sm font-medium">{config.code === 'PT' ? 'João Santos' : 'João Silva'}</p>
                        <p className="text-xs text-slate-500">{config.terms.motoboy.charAt(0).toUpperCase() + config.terms.motoboy.slice(1)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Código</p>
                      <p className="text-lg font-mono font-bold text-orange-600">847291</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating cards */}
              <div className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Entrega Confirmada</p>
                    <p className="text-xs text-slate-500">Há 2 minutos</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">+15 pedidos hoje</p>
                    <p className="text-xs text-slate-500">Sua empresa</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Segmentos Atendidos */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Para <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">Diversos Segmentos</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Nossa plataforma atende empresas de todos os tamanhos e segmentos que precisam de entregas rápidas e confiáveis.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { icon: "🍕", name: "Restaurantes", desc: "Pizzarias, fast-food" },
              { icon: "💊", name: "Farmácias", desc: "Medicamentos" },
              { icon: "🛒", name: "Supermercados", desc: "Compras delivery" },
              { icon: "🌸", name: "Floristas", desc: "Arranjos florais" },
              { icon: "🐕", name: "Pet Shops", desc: "Produtos pet" },
              { icon: "📦", name: "Lojas", desc: "E-commerce local" },
              { icon: "🍰", name: "Confeitarias", desc: "Doces e bolos" },
              { icon: "🥖", name: "Padarias", desc: "Pães e lanches" },
              { icon: "🥗", name: "Marmitas", desc: "Refeições" },
              { icon: "📄", name: "Escritórios", desc: "Documentos" },
              { icon: "🔧", name: "Autopeças", desc: "Peças veiculares" },
              { icon: "🎁", name: "Presentes", desc: "Cestas e kits" }
            ].map((segment, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-4 text-center hover:shadow-lg hover:border-orange-200 border border-transparent transition-all cursor-default"
              >
                <div className="text-3xl mb-2">{segment.icon}</div>
                <p className="font-medium text-slate-800 text-sm">{segment.name}</p>
                <p className="text-xs text-slate-500">{segment.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Tudo que você precisa para
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500"> suas entregas</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Uma plataforma completa para conectar sua empresa a {config.terms.entregadores} de forma segura e eficiente.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group p-6 bg-white rounded-2xl border border-slate-200 hover:border-orange-200 hover:shadow-lg hover:shadow-orange-100/50 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-4 group-hover:bg-gradient-to-br group-hover:from-orange-600 group-hover:to-amber-600 group-hover:text-white transition-all">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-orange-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Como <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">Funciona</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comece a usar em poucos minutos. Processo simples e rápido.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-orange-200 -translate-x-1/2 z-0"></div>
                )}
                <div className="relative bg-white rounded-2xl p-6 border border-slate-200 z-10">
                  <div className="text-5xl font-bold text-orange-100 mb-4">{item.step}</div>
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-600 to-amber-500 rounded-xl flex items-center justify-center text-white mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Planos <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">Transparentes</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Escolha o plano ideal para seu negócio. Sem taxas escondidas.
            </p>
          </div>
          
          {/* Plan Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-slate-100 p-1 rounded-xl inline-flex">
              <button
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'cliente' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setActiveTab('cliente')}
              >
                Sou Restaurante/Empresa
              </button>
              <button
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'entregador' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                onClick={() => setActiveTab('entregador')}
              >
                {config.terms.souEntregador}
              </button>
            </div>
          </div>
          
          {/* Plan Card */}
          <div className="max-w-md mx-auto">
            <div className={`bg-white rounded-3xl border-2 ${
              activeTab === 'cliente' ? 'border-orange-200' : 'border-green-200'
            } p-8 shadow-xl`}>
              <div className={`inline-block px-4 py-1 rounded-full text-sm font-medium mb-4 ${
                activeTab === 'cliente' 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {plans[activeTab].subtitle}
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{plans[activeTab].name}</h3>
              
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-sm text-slate-500">{config.currency.symbol}</span>
                <span className="text-5xl font-bold text-slate-900">{plans[activeTab].price}</span>
                <span className="text-slate-500">{plans[activeTab].period}</span>
              </div>
              
              <p className={`text-sm font-medium mb-6 ${
                activeTab === 'cliente' ? 'text-orange-600' : 'text-green-600'
              }`}>
                {plans[activeTab].extra}
              </p>
              
              <ul className="space-y-4 mb-6">
                {plans[activeTab].features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className={`w-5 h-5 ${
                      activeTab === 'cliente' ? 'text-orange-500' : 'text-green-500'
                    }`} />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* Bônus exclusivos para entregadores */}
              {activeTab === 'entregador' && plans.entregador.bonuses && (
                <div className="mb-6 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <p className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Bônus Exclusivos
                  </p>
                  <ul className="space-y-2">
                    {plans.entregador.bonuses.map((bonus, index) => (
                      <li key={index} className={`flex items-start gap-2 text-sm ${
                        bonus.highlight ? 'text-green-700 font-medium' : 'text-slate-600'
                      }`}>
                        <span className="text-lg leading-none">{bonus.icon}</span>
                        <span>{bonus.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button 
                size="lg"
                className={`w-full h-14 text-lg ${
                  activeTab === 'cliente' 
                    ? 'bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-700 hover:to-amber-700' 
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                onClick={() => navigate('/login')}
              >
                Começar Agora
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-orange-600 to-amber-500 rounded-3xl p-8 md:p-12 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-4">
                  <Smartphone className="w-4 h-4 text-white" />
                  <span className="text-sm font-medium text-white">Disponível para Android e iOS</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Baixe o App Rota 017
                </h2>
                <p className="text-lg text-orange-100 mb-6">
                  Tenha a plataforma na palma da sua mão. Receba notificações em tempo real, 
                  aceite entregas e gerencie sua equipe de qualquer lugar.
                </p>
                
                <ul className="space-y-2 mb-6 text-white/90">
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Notificações instantâneas de novas entregas</span>
                  </li>
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Funciona offline com dados salvos</span>
                  </li>
                  <li className="flex items-center gap-2 justify-center md:justify-start">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span>Acesso rápido direto da {config.terms.telaInicial}</span>
                  </li>
                </ul>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  {!isInstalled ? (
                    <Button 
                      size="lg"
                      className="bg-white text-orange-600 hover:bg-orange-50 h-14 px-8 text-lg shadow-lg"
                      onClick={handleInstallApp}
                      data-testid="landing-install-app-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Instalar Agora
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 bg-green-500/20 text-white px-6 py-3 rounded-lg">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">App Instalado!</span>
                    </div>
                  )}
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/50 text-white hover:bg-white/10 h-14 px-8 text-lg"
                    onClick={() => navigate('/app/entregador')}
                  >
                    {config.code === 'PT' ? 'Acessar App Estafeta' : 'Acessar App Entregador'}
                  </Button>
                </div>
              </div>
              
              {/* Phone Mockup */}
              <div className="hidden md:block">
                <div className="w-64 h-auto bg-slate-800 rounded-[2.5rem] p-3 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                  <div className="bg-slate-900 rounded-[2rem] p-4 h-[420px] flex flex-col">
                    {/* Status bar */}
                    <div className="flex justify-between items-center text-white/60 text-xs mb-4">
                      <span>9:41</span>
                      <div className="flex gap-1">
                        <div className="w-4 h-2 bg-white/60 rounded-sm" />
                        <div className="w-4 h-2 bg-white/60 rounded-sm" />
                        <div className="w-6 h-3 bg-green-400 rounded-sm" />
                      </div>
                    </div>
                    
                    {/* App Header */}
                    <div className="flex items-center gap-2 mb-4">
                      <img src="/logo-rota017.png" alt="Rota 017" className="w-8 h-8 rounded-lg object-cover" />
                      <span className="text-white font-bold">Rota 017</span>
                    </div>
                    
                    {/* Notification Card */}
                    <div className="bg-orange-500/20 border border-orange-500/30 rounded-xl p-3 mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                        <span className="text-orange-300 text-xs font-medium">{config.terms.novaEntrega}</span>
                      </div>
                      <p className="text-white/80 text-sm">{config.code === 'PT' ? 'Farmácia Central' : 'Farmácia Popular'}</p>
                      <p className="text-white/60 text-xs">{config.code === 'PT' ? 'Av. da Liberdade, 100' : 'Av. Paulista, 1000'}</p>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-white">12</p>
                        <p className="text-xs text-white/60">{config.terms.entregasHoje}</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-green-400">{config.code === 'PT' ? '€ 45' : 'R$ 180'}</p>
                        <p className="text-xs text-white/60">{config.terms.ganhos}</p>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <div className="mt-auto">
                      <div className="bg-green-500 rounded-xl py-3 text-center">
                        <span className="text-white font-medium">{config.terms.verEntregasDisponiveis}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-600 to-amber-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pronto para Agilizar suas Entregas?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Junte-se a centenas de empresas que já confiam na Rota 017.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-orange-600 hover:bg-orange-50 h-14 px-8 text-lg"
              onClick={() => navigate('/login')}
            >
              Criar Conta Grátis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 h-14 px-8 text-lg"
              onClick={() => navigate('/suporte')}
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Fale Conosco
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover" />
                <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Rota 017
                </span>
                <span className="text-xl ml-1">{config.flag}</span>
              </div>
              <p className="text-slate-400 mb-6 max-w-md">
                Plataforma de entregas B2B. Conectamos empresas de todos os segmentos a {config.terms.entregadores} verificados de forma segura e eficiente.
              </p>
              <Button 
                variant="outline"
                onClick={() => navigate('/suporte')}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Fale Conosco
              </Button>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Plataforma</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="#features" className="hover:text-orange-400 transition-colors">Recursos</a></li>
                <li><a href="#pricing" className="hover:text-orange-400 transition-colors">Planos</a></li>
                <li><a href="/suporte" className="hover:text-orange-400 transition-colors">Suporte</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-slate-400">
                <li><a href="/privacidade" className="hover:text-orange-400 transition-colors">Política de Privacidade</a></li>
                <li><a href="#" className="hover:text-orange-400 transition-colors">Termos de Uso</a></li>
                {config.code === 'PT' && (
                  <li><a href="/privacidade" className="hover:text-orange-400 transition-colors">RGPD</a></li>
                )}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © 2026 Rota 017. Todos os direitos reservados.
            </p>
            <p className="text-slate-500 text-sm">
              {config.code === 'PT' ? 'Entregas B2B • Sem vínculo laboral • 100% Pré-pago' : 'Entregas B2B • Sem vínculo empregatício • 100% Pré-pago'}
            </p>
          </div>
        </div>
      </footer>

      {/* Modal de Instruções iOS */}
      {showIOSModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-amber-500 rounded-xl flex items-center justify-center">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900">Instalar no iPhone/iPad</h3>
                <p className="text-sm text-slate-500">Siga os passos abaixo</p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <div>
                  <p className="font-medium text-slate-900">Toque no botão Compartilhar</p>
                  <p className="text-sm text-slate-500">Ícone de quadrado com seta para cima na barra do Safari</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <div>
                  <p className="font-medium text-slate-900">{config.code === 'PT' ? 'Selecione "Adicionar ao ecrã inicial"' : 'Selecione "Adicionar à Tela de Início"'}</p>
                  <p className="text-sm text-slate-500">{config.code === 'PT' ? 'Deslize para baixo no menu se necessário' : 'Role para baixo no menu se necessário'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <div>
                  <p className="font-medium text-slate-900">Toque em "Adicionar"</p>
                  <p className="text-sm text-slate-500">O app aparecerá no seu {config.terms.telaInicial}</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={() => setShowIOSModal(false)}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              Entendi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
